import React, { useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// MODO FOCO — cronômetro de leitura. Dois modos:
//   • Cronômetro (count-up): "iniciar leitura" → conta pra frente.
//   • Pomodoro (de verdade): foco → pausa → foco → ... por N ciclos.
//     SÓ o tempo de FOCO conta como leitura (a pausa NÃO conta). A última pausa
//     é omitida. Pausa fica visualmente fria/calma (prata, glow lento, ":" pisca).
// Design circular minimalista, com glow respirando, ponteiro na ponta do arco,
// cycle-dots de progresso e tela de conclusão. Devolve os minutos lidos.
// ─────────────────────────────────────────────────────────────────────────────

const EMBER = '232,122,59';
const R = 108;
const CIRC = 2 * Math.PI * R;

const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (sec: number) => `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;
const fmtTotal = (min: number) => min >= 60 ? `${Math.floor(min / 60)}h${pad(min % 60)}` : `${min} min`;

const FOCO_MSGS = ['Boa leitura. Eu cuido do tempo.', 'Mergulha no livro — eu te aviso.', 'Aproveita o silêncio.', 'Página por página.'];
const PAUSA_MSGS = ['Respira. Descansa os olhos.', 'Levanta, alonga, bebe água.', 'Sem pressa — a pausa não conta no tempo lido.'];

// ─────────────────────────────────────────────────────────────────────────────
// O TEMPO VEM DO RELÓGIO DO APARELHO, NÃO DE UM CONTADOR (pente fino, QA-19).
//
// Antes, um setInterval somava +1 por segundo. Com o celular bloqueado (o que
// o próprio app pede: "deixe o celular de lado"), o iOS e o Android congelam o
// JavaScript e o contador PARA: 37 s reais viravam 7 s na tela e no registro.
// Agora a sessão é um retrato com carimbos de tempo — quando a fase começou a
// correr e quanto já tinha acumulado — e o tempo é sempre (agora − início).
// O intervalo só redesenha a tela. Mesmo jeito da sessão do Protocolo.
//
// E O RETRATO FICA NO APARELHO (QA-20): fechar o app no meio não perde a
// leitura. Ao reabrir, o App oferece registrar (ou continuar). Mais de 12 h
// parado: descarta.
// ─────────────────────────────────────────────────────────────────────────────

export type SessaoFoco = {
  v: 1;
  bookId: string | null;
  bookTitle?: string;
  mode: 'countup' | 'pomodoro';
  focoMin: number; pausaMin: number; ciclos: number;
  phase: 'running' | 'paused' | 'done';
  kind: 'foco' | 'pausa';
  cycleIdx: number;
  faseAcum: number;          // seg já acumulados na fase atual antes de faseInicio
  faseInicio: number | null; // ms em que a fase voltou a correr (null = parada)
  focoFeito: number;         // seg de FOCO das fases já concluídas (pomodoro)
  iniciadaEm: number;
  salvoEm: number;
};

const CHAVE_SESSAO = 'forjamente.foco.sessao';
const VALIDADE_MS = 12 * 3600 * 1000;

/** A sessão de foco guardada no aparelho (null se não há, ou se passou de 12 h). */
export function lerSessaoFoco(): SessaoFoco | null {
  try {
    const s = JSON.parse(localStorage.getItem(CHAVE_SESSAO) || 'null');
    if (!s || s.v !== 1) return null;
    if (Date.now() - (s.salvoEm || 0) > VALIDADE_MS) { localStorage.removeItem(CHAVE_SESSAO); return null; }
    return s;
  } catch { return null; }
}
export function descartarSessaoFoco() { try { localStorage.removeItem(CHAVE_SESSAO); } catch {} }
function guardarSessaoFoco(s: SessaoFoco) { try { localStorage.setItem(CHAVE_SESSAO, JSON.stringify({ ...s, salvoEm: Date.now() })); } catch {} }

/** Aplica o relógio ao retrato: vira as fases do pomodoro que já passaram. */
export function pelaHora(s: SessaoFoco, agora: number): SessaoFoco {
  let n = { ...s };
  for (let guarda = 0; guarda < 200; guarda++) {
    if (n.mode !== 'pomodoro' || n.phase !== 'running') break;
    const decorrido = n.faseAcum + (n.faseInicio ? (agora - n.faseInicio) / 1000 : 0);
    const alvo = (n.kind === 'foco' ? n.focoMin : n.pausaMin) * 60;
    if (decorrido < alvo) break;
    const sobra = decorrido - alvo;
    if (n.kind === 'foco') {
      n.focoFeito += n.focoMin * 60;
      if (n.cycleIdx >= n.ciclos - 1) { n = { ...n, phase: 'done', faseAcum: alvo, faseInicio: null }; break; }
      n = { ...n, kind: 'pausa', faseAcum: sobra, faseInicio: agora };
    } else {
      n = { ...n, kind: 'foco', cycleIdx: n.cycleIdx + 1, faseAcum: sobra, faseInicio: agora };
    }
  }
  return n;
}
/** Segundos da fase atual (contagem livre: o total). */
export function segundosDaFase(s: SessaoFoco, agora: number): number {
  return Math.max(0, Math.floor(s.faseAcum + (s.faseInicio ? (agora - s.faseInicio) / 1000 : 0)));
}
/** Segundos LIDOS da sessão: contagem livre = tudo; pomodoro = só o foco. */
export function segundosLidos(s: SessaoFoco, agora: number): number {
  const n = pelaHora(s, agora);
  if (n.mode !== 'pomodoro') return segundosDaFase(n, agora);
  const atual = n.phase !== 'done' && n.kind === 'foco' ? Math.min(segundosDaFase(n, agora), n.focoMin * 60) : 0;
  return Math.floor(n.focoFeito + atual);
}

export const FocusTimer: React.FC<{
  /** Rótulo da saída ANTES de começar. O modal de registro agora abre direto
   *  aqui, então "Cancelar" passaria a mentir: quem toca não está desistindo do
   *  registro, está dizendo "não quero cronômetro, só lançar a página". */
  rotuloSaida?: string;
  bookTitle?: string;
  /** O livro da sessão: é o que liga a sessão guardada no aparelho ao livro. */
  bookId?: string | null;
  /** Reabriu o app e pediu "Registrar": restaura a sessão guardada e já encerra. */
  encerrarAoRestaurar?: boolean;
  onFinish: (minutes: number, seconds: number) => void;
  onCancel: () => void;
}> = ({ bookTitle, bookId = null, encerrarAoRestaurar = false, onFinish, onCancel, rotuloSaida = 'Cancelar' }) => {
  // Sessão guardada deste livro? Retoma de onde estava (pelo relógio).
  const [guardada] = useState<SessaoFoco | null>(() => {
    const g = lerSessaoFoco();
    return g && bookId && g.bookId === bookId ? g : null;
  });
  const [mode, setMode] = useState<'countup' | 'pomodoro'>(guardada?.mode || 'countup');
  const [focoMin, setFocoMin] = useState(guardada?.focoMin || 25);
  const [pausaMin, setPausaMin] = useState(guardada?.pausaMin || 5);
  const [ciclos, setCiclos] = useState(guardada?.ciclos || 4);
  const [sessao, setSessao] = useState<SessaoFoco | null>(guardada);
  const [agora, setAgora] = useState(() => Date.now());
  const tick = useRef<any>(null);

  const isPomo = mode === 'pomodoro';
  const atual = sessao ? pelaHora(sessao, agora) : null;
  const phase: 'setup' | 'running' | 'paused' | 'done' = atual ? atual.phase : 'setup';
  const kind = atual ? atual.kind : 'foco';
  const cycleIdx = atual ? atual.cycleIdx : 0;
  const elapsed = atual ? segundosDaFase(atual, agora) : 0;
  const readSec = atual ? segundosLidos(atual, agora) : 0;

  // O intervalo só REDESENHA; o tempo é sempre lido do relógio. Ao voltar do
  // bloqueio (visibilitychange), redesenha na hora com o tempo real.
  useEffect(() => {
    if (phase !== 'running') return;
    const redesenhar = () => setAgora(Date.now());
    tick.current = setInterval(redesenhar, 500);
    const aoVoltar = () => { if (document.visibilityState === 'visible') redesenhar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', redesenhar);
    return () => { clearInterval(tick.current); document.removeEventListener('visibilitychange', aoVoltar); window.removeEventListener('focus', redesenhar); };
  }, [phase]);

  // A fase virou pelo relógio (pomodoro)? Fixa o retrato novo.
  useEffect(() => {
    if (sessao && atual && (atual.kind !== sessao.kind || atual.cycleIdx !== sessao.cycleIdx || atual.phase !== sessao.phase)) setSessao(atual);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Guarda no aparelho a cada mudança de estado (não a cada segundo: os
  // carimbos já bastam para recalcular o tempo).
  useEffect(() => { if (sessao) guardarSessaoFoco(sessao); }, [sessao]);

  const targetSec = isPomo ? (kind === 'foco' ? focoMin * 60 : pausaMin * 60) : 0;
  const remaining = isPomo ? Math.max(0, targetSec - elapsed) : 0;
  const isBreak = isPomo && kind === 'pausa' && (phase === 'running' || phase === 'paused');

  const running = phase === 'running';
  const start = () => {
    const t = Date.now();
    setAgora(t);
    setSessao({ v: 1, bookId, bookTitle, mode, focoMin, pausaMin, ciclos, phase: 'running', kind: 'foco', cycleIdx: 0, faseAcum: 0, faseInicio: t, focoFeito: 0, iniciadaEm: t, salvoEm: t });
  };
  const pausarOuRetomar = () => {
    const t = Date.now();
    setAgora(t);
    setSessao((s) => {
      if (!s) return s;
      const n = pelaHora(s, t);
      if (n.phase === 'running') return { ...n, phase: 'paused', faseAcum: n.faseAcum + (n.faseInicio ? (t - n.faseInicio) / 1000 : 0), faseInicio: null };
      if (n.phase === 'paused') return { ...n, phase: 'running', faseInicio: t };
      return n;
    });
  };
  const encerrar = (seg: number) => { descartarSessaoFoco(); setSessao(null); onFinish(Math.max(1, Math.round(seg / 60)), seg); };
  const finishCountup = () => encerrar(elapsedAgora());
  const finishPomoNow = () => encerrar(lidosAgora());
  const registerDone = () => encerrar(lidosAgora());
  const descartar = () => { descartarSessaoFoco(); setSessao(null); onCancel(); };
  const skipBreak = () => {
    const t = Date.now();
    setAgora(t);
    setSessao((s) => {
      if (!s) return s;
      const n = pelaHora(s, t);
      if (n.kind !== 'pausa') return n;
      return { ...n, kind: 'foco', cycleIdx: n.cycleIdx + 1, faseAcum: 0, faseInicio: t, phase: 'running' };
    });
  };
  function elapsedAgora() { return sessao ? segundosDaFase(pelaHora(sessao, Date.now()), Date.now()) : 0; }
  function lidosAgora() { return sessao ? segundosLidos(sessao, Date.now()) : 0; }

  // "Registrar" pedido ao reabrir o app: restaura e encerra com o tempo real.
  const encerrou = useRef(false);
  useEffect(() => {
    if (encerrarAoRestaurar && guardada && !encerrou.current) {
      encerrou.current = true;
      encerrar(guardada.mode === 'pomodoro' ? segundosLidos(guardada, Date.now()) : segundosDaFase(pelaHora(guardada, Date.now()), Date.now()));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // tempo + progresso do anel
  const shownSec = isPomo ? (phase === 'done' ? readSec : remaining) : elapsed;
  const progress = phase === 'done' ? 1 : isPomo ? (targetSec ? remaining / targetSec : 0) : (elapsed % 3600) / 3600;
  const dashoffset = (mode === 'countup' && phase === 'setup') ? CIRC : CIRC * (1 - progress);

  // ponteiro na ponta do arco (foco rodando ou count-up rodando)
  const tipAngle = (-90 + 360 * progress) * Math.PI / 180;
  const tipX = 130 + R * Math.cos(tipAngle);
  const tipY = 130 + R * Math.sin(tipAngle);
  const showTip = running && (mode === 'countup' || (isPomo && kind === 'foco'));

  const ringAnim = phase === 'running'
    ? (isBreak ? 'fjCalm 6s ease-in-out infinite' : 'fjBreath 3.2s ease-in-out infinite')
    : 'none';

  // textos
  const headerTxt = phase === 'done' ? 'Concluído'
    : phase === 'setup' ? 'Modo foco'
    : isPomo ? `${kind === 'foco' ? 'Foco' : 'Pausa'} · ${cycleIdx + 1} de ${ciclos}`
    : 'Lendo';
  const centerSub = phase === 'done' ? 'ciclos concluídos'
    : phase === 'setup' ? (isPomo ? `meta ${focoMin} min` : 'pronto pra começar')
    : isPomo ? (kind === 'foco' ? 'minutos de foco' : 'pausa · respire')
    : 'minutos lidos';
  const micro = isBreak ? PAUSA_MSGS[cycleIdx % PAUSA_MSGS.length]
    : (isPomo && kind === 'foco' && (running || phase === 'paused')) ? FOCO_MSGS[cycleIdx % FOCO_MSGS.length]
    : null;
  const timeColor = phase === 'done' ? 'var(--ink)' : isBreak ? 'var(--ink-2)' : 'var(--ink)';
  const totalMin = Math.max(1, Math.round(readSec / 60));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0 6px' }}>
      <style>{`
        @keyframes fjBreath { 0%,100%{ filter: drop-shadow(0 0 10px rgba(${EMBER},0.25)); } 50%{ filter: drop-shadow(0 0 26px rgba(${EMBER},0.55)); } }
        @keyframes fjCalm { 0%,100%{ filter: drop-shadow(0 0 8px rgba(243,236,222,0.06)); } 50%{ filter: drop-shadow(0 0 16px rgba(243,236,222,0.16)); } }
        @keyframes fjColon { 0%,100%{ opacity: 1; } 50%{ opacity: 0.4; } }
        @keyframes fjFade { from{opacity:0; transform:translateY(6px);} to{opacity:1; transform:none;} }
        @keyframes fjPulse { 0%{ transform:scale(0.96); opacity:0.7; } 50%{ transform:scale(1.04); } 100%{ transform:scale(1); opacity:1; } }
      `}</style>

      {/* cabeçalho */}
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: isBreak ? 'var(--ink-4)' : 'var(--ink-3)' }}>{headerTxt}</div>
      {bookTitle && phase === 'setup' && <div className="serif" style={{ fontSize: 16, color: 'var(--ink-2)', marginTop: 3, maxWidth: 260, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bookTitle}</div>}

      {/* cycle dots (pomodoro rodando/pausado/done) */}
      {isPomo && phase !== 'setup' && (
        <div style={{ display: 'flex', gap: 7, height: 12, alignItems: 'center', marginTop: 8 }}>
          {Array.from({ length: ciclos }).map((_, i) => {
            const done = phase === 'done' || i < cycleIdx;
            const cur = phase !== 'done' && i === cycleIdx;
            const sz = cur && kind === 'pausa' ? 5 : 6;
            const color = done ? 'var(--ember)' : cur ? (kind === 'foco' ? 'var(--ember)' : 'var(--ink-4)') : 'var(--paper-3)';
            return <div key={i} style={{ width: sz, height: sz, borderRadius: '50%', background: color, transition: 'all 400ms ease', boxShadow: cur && kind === 'foco' ? `0 0 6px rgba(${EMBER},0.8)` : 'none', animation: cur && kind === 'foco' && running ? 'fjBreath 3.2s ease-in-out infinite' : 'none' }} />;
          })}
        </div>
      )}

      {/* anel */}
      <div style={{ position: 'relative', width: 260, height: 260, margin: '12px 0 4px' }}>
        {/* halo frio só na pausa */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'radial-gradient(circle, rgba(243,236,222,0.05), transparent 70%)', opacity: isBreak ? 1 : 0, transition: 'opacity 600ms' }} />
        <svg width={260} height={260} viewBox="0 0 260 260" style={{ animation: ringAnim, position: 'relative' }}>
          <defs>
            <linearGradient id="fjRing" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f0a877" /><stop offset="100%" stopColor="#e8702a" />
            </linearGradient>
          </defs>
          <circle cx={130} cy={130} r={R} fill="none" stroke="var(--paper-3)" strokeWidth={11} opacity={0.6} />
          <circle key={`prog-${phase}-${kind}-${cycleIdx}`} cx={130} cy={130} r={R} fill="none"
            stroke={isBreak ? 'var(--ink-3)' : 'url(#fjRing)'} strokeOpacity={isBreak ? 0.5 : 1}
            strokeWidth={11} strokeLinecap="round"
            strokeDasharray={CIRC} strokeDashoffset={dashoffset}
            transform="rotate(-90 130 130)" style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.4s' }} />
          {showTip && <circle cx={tipX} cy={tipY} r={6.5} fill="#e8702a" style={{ filter: `drop-shadow(0 0 8px rgba(${EMBER},0.9))`, transition: 'cx 0.9s linear, cy 0.9s linear' }} />}
        </svg>
        {/* centro */}
        <div key={isPomo ? `${kind}${cycleIdx}${phase}` : phase} style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', animation: 'fjFade 280ms ease-out' }}>
          {phase === 'done'
            ? <>
                <CheckIcon />
                <div className="serif" style={{ fontSize: 46, lineHeight: 1, letterSpacing: '-0.02em', color: 'var(--ink)', marginTop: 4 }}>{fmtTotal(totalMin)}</div>
              </>
            : <div className="serif" style={{ fontSize: 56, lineHeight: 1, letterSpacing: '-0.02em', color: timeColor }}>
                {pad(Math.floor(shownSec / 60))}<span style={{ color: 'var(--ink-3)', animation: isBreak ? 'fjColon 2s ease-in-out infinite' : 'none' }}>:</span>{pad(shownSec % 60)}
              </div>}
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)', marginTop: 8, textAlign: 'center' }}>{centerSub}</div>
        </div>
      </div>

      {/* microcopy gentil abaixo do anel */}
      {micro && <div key={`m${kind}${cycleIdx}`} style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center', lineHeight: 1.35, maxWidth: 280, marginTop: 2, animation: 'fjFade 280ms ease-out' }}>{micro}</div>}
      {phase === 'done' && <div className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)', marginTop: 6 }}>Você leu {totalMin} min em {ciclos} {ciclos > 1 ? 'ciclos' : 'ciclo'}</div>}

      <div style={{ width: '100%', marginTop: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        {/* ─────────── SETUP ─────────── */}
        {phase === 'setup' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, animation: 'fjFade 240ms ease-out' }}>
            <div style={{ display: 'flex', gap: 4, background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 13, padding: 4 }}>
              {([['countup', 'Cronômetro'], ['pomodoro', 'Pomodoro']] as const).map(([m, label]) => (
                <button key={m} onClick={() => setMode(m)} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, background: mode === m ? 'var(--ember-soft)' : 'transparent', color: mode === m ? 'var(--ember-2)' : 'var(--ink-3)' }}>{label}</button>
              ))}
            </div>

            {isPomo && (
              <>
                <div style={{ width: '100%', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 16, padding: '2px 14px' }}>
                  {([
                    { label: 'Foco', sub: 'tempo de leitura', val: focoMin, set: setFocoMin, min: 5, max: 90, step: 5, unit: 'min' },
                    { label: 'Pausa', sub: 'pra descansar os olhos', val: pausaMin, set: setPausaMin, min: 1, max: 30, step: 1, unit: 'min' },
                    { label: 'Ciclos', sub: 'quantas rodadas', val: ciclos, set: setCiclos, min: 1, max: 8, step: 1, unit: '' },
                  ] as const).map((row, i) => (
                    <div key={row.label} style={{ height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: i < 2 ? '1px solid var(--line)' : 'none' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{row.label}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{row.sub}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StepBtn dir="-" disabled={row.val <= row.min} onClick={() => row.set(Math.max(row.min, row.val - row.step))} />
                        <div style={{ width: 64, textAlign: 'center' }}>
                          <span className="serif" style={{ fontSize: 26, color: 'var(--ink)' }}>{row.val}</span>
                          {row.unit && <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 2 }}>{row.unit}</span>}
                        </div>
                        <StepBtn dir="+" disabled={row.val >= row.max} onClick={() => row.set(Math.min(row.max, row.val + row.step))} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)', textAlign: 'center' }}>
                  {ciclos} {ciclos > 1 ? 'ciclos' : 'ciclo'} <span style={{ color: 'var(--ink-3)', margin: '0 6px' }}>·</span> <span style={{ color: 'var(--ember-2)' }}>{fmtTotal(focoMin * ciclos)}</span> de leitura
                </div>
              </>
            )}

            <button onClick={start} style={primaryBtn}><PlayIcon /> {isPomo ? 'Iniciar pomodoro' : 'Iniciar leitura'}</button>
            <button onClick={onCancel} style={ghostLink}>{rotuloSaida}</button>
          </div>
        )}

        {/* ─────────── RODANDO / PAUSADO ─────────── */}
        {(phase === 'running' || phase === 'paused') && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, animation: 'fjFade 240ms ease-out' }}>
            <div style={{ display: 'flex', gap: 12, width: '100%' }}>
              <button onClick={pausarOuRetomar} style={{ ...secondaryBtn, flex: 1 }}>
                {running ? <><PauseIcon /> Pausar</> : <><PlayIcon /> Retomar</>}
              </button>
              {isBreak
                ? <button onClick={skipBreak} style={{ ...secondaryBtn, flex: 1, border: '1px solid var(--ember-soft)', color: 'var(--ember-2)' }}><SkipIcon /> Pular pausa</button>
                : <button onClick={isPomo ? finishPomoNow : finishCountup} style={{ ...primaryBtn, flex: 1, margin: 0 }}>Terminei</button>}
            </div>
            {isBreak
              ? <div style={{ display: 'flex', gap: 18 }}>
                  <button onClick={finishPomoNow} style={ghostLink}>Terminei a leitura</button>
                  <button onClick={descartar} style={ghostLink}>Descartar</button>
                </div>
              : <button onClick={descartar} style={ghostLink}>Descartar sessão</button>}
          </div>
        )}

        {/* ─────────── CONCLUSÃO ─────────── */}
        {phase === 'done' && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, animation: 'fjFade 280ms ease-out' }}>
            <button onClick={registerDone} style={primaryBtn}>Registrar leitura</button>
          </div>
        )}
      </div>
    </div>
  );
};

const StepBtn: React.FC<{ dir: '-' | '+'; disabled?: boolean; onClick: () => void }> = ({ dir, disabled, onClick }) => (
  <button onClick={onClick} disabled={disabled} style={{
    width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--line)', background: 'var(--paper-3)',
    color: 'var(--ink-2)', fontSize: 18, lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.3 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>{dir === '-' ? '−' : '+'}</button>
);

const PlayIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;
const PauseIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>;
const SkipIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5v14l8.5-7L6 5zM16 5h2.5v14H16z" /></svg>;
const CheckIcon = () => <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#e8702a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7" /></svg>;

const primaryBtn: React.CSSProperties = { width: '100%', border: 'none', cursor: 'pointer', borderRadius: 16, padding: 16, background: 'var(--ember)', color: '#16110d', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, boxShadow: `0 14px 30px -10px rgba(${EMBER},0.6)` };
const secondaryBtn: React.CSSProperties = { border: '1px solid var(--line)', cursor: 'pointer', borderRadius: 16, padding: 16, background: 'var(--paper-2)', color: 'var(--ink)', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
const ghostLink: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--ink-3)', fontSize: 13, cursor: 'pointer', padding: 6 };
